import React, { useEffect, useMemo, useState } from 'react';
import ChoicePicker from '../components/ChoicePicker';
import TextInput from '../components/TextInput';
import WheelDatePicker, { type WheelDateValue } from '../components/WheelDatePicker';
import { sendIcon } from '../chatIcons';
import type { FormFlowStep } from '../../../../../types/formFlow';

export interface WorkerRegistryComposerProps {
  step: FormFlowStep;
  value: string;
  onChange: (fieldName: string, value: string) => void;
  onSubmit: () => void;
  canSubmit: boolean;
  wheelLocale: string;
  t: (key: string) => string;
  sendAriaLabel: string;
  /** When the full question is already shown in the chat thread, hide the long prompt in the composer. */
  hideDuplicateThreadPrompt?: boolean;
}

function parseIsoDate(s: string): WheelDateValue | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  return { year: y, month: mo, day: d };
}

const WorkerRegistryComposer: React.FC<WorkerRegistryComposerProps> = ({
  step,
  value,
  onChange,
  onSubmit,
  canSubmit,
  wheelLocale,
  t,
  sendAriaLabel,
  hideDuplicateThreadPrompt,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const promptFallback = useMemo(() => {
    const k = step.promptKey;
    const tr = t(k);
    if (tr !== k) return tr;
    return step.fieldName;
  }, [step.fieldName, step.promptKey, t]);

  const compactFieldLabel = useMemo(
    () => t('submitDocuments.workerRegistryChat.compactInputLabel'),
    [t]
  );

  const textFieldLabel = hideDuplicateThreadPrompt ? compactFieldLabel : promptFallback;

  // Date steps: seed ISO from wheel defaults so answers stay in sync with the picker
  useEffect(() => {
    if (step.fieldType !== 'date') return;
    if (value) return;
    const today = new Date();
    const iso = `${today.getFullYear()}-01-01`;
    onChange(step.fieldName, iso);
  }, [step.fieldName, step.fieldType, value, onChange]);

  if (step.fieldType === 'choice' && step.options?.length) {
    const selected = value || null;
    return (
      <div className="ff-text-input">
        {!hideDuplicateThreadPrompt && (
          <p className="wr-composer__prompt" style={{ padding: '0 12px 8px', fontSize: 13, margin: 0 }}>
            {promptFallback}
          </p>
        )}
        <div className="ff-text-input__fields">
          <ChoicePicker
            value={selected}
            options={step.options.map((o) => ({
              value: o.value,
              label: (o.labelKey && t(o.labelKey) !== o.labelKey ? t(o.labelKey) : null) || o.label || o.value,
            }))}
            onChange={(v) => onChange(step.fieldName, v)}
          />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => {
              setSubmitAttempted(true);
              if (canSubmit) onSubmit();
            }}
            disabled={!canSubmit}
            aria-label={sendAriaLabel}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
        {submitAttempted && step.required && !canSubmit && (
          <p style={{ color: '#c94c5a', fontSize: 12, padding: '0 12px' }} role="alert">
            {t('submitDocuments.workerRegistryChat.required')}
          </p>
        )}
      </div>
    );
  }

  if (step.fieldType === 'date') {
    const parsed = parseIsoDate(value);
    const today = new Date();
    const wheel: WheelDateValue = parsed ?? {
      year: today.getFullYear(),
      month: 1,
      day: 1,
    };
    return (
      <div className="ff-text-input">
        {!hideDuplicateThreadPrompt && (
          <p className="wr-composer__prompt" style={{ padding: '0 12px 8px', fontSize: 13, margin: 0 }}>
            {promptFallback}
          </p>
        )}
        <div className="ff-text-input__fields" style={{ padding: '0 8px' }}>
          <WheelDatePicker value={wheel} locale={wheelLocale} onChange={(next) => {
            const iso = `${next.year}-${String(next.month).padStart(2, '0')}-${String(next.day).padStart(2, '0')}`;
            onChange(step.fieldName, iso);
          }} />
        </div>
        <div className="ff-text-input__footer">
          <button type="button" className="ff-text-input__send" onClick={onSubmit} aria-label={sendAriaLabel}>
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (step.fieldType === 'number') {
    if (step.fieldName === 'constructionExperienceYears') {
      const skipped = value.trim().length === 0;
      const numberError =
        !skipped && value.trim().length > 0
          ? (() => {
              const v = value.trim();
              if (!/^\d+$/.test(v)) return t('submitDocuments.workerRegistryChat.invalidNumber');
              const n = Number(v);
              if (step.min != null && n < step.min) return t('submitDocuments.workerRegistryChat.invalidNumber');
              if (step.max != null && n > step.max) return t('submitDocuments.workerRegistryChat.invalidNumber');
              return null;
            })()
          : null;
      const canSendExperience = skipped || (value.trim().length > 0 && !numberError);

      return (
        <div className="ff-text-input">
          <div className="ff-text-input__fields">
            <ChoicePicker
              value={skipped ? 'skip' : null}
              options={[
                {
                  value: 'skip',
                  label: t('forms.workerRegistry.constructionExperienceYears.skipped'),
                },
              ]}
              onChange={() => onChange(step.fieldName, '')}
            />
            <div className="ff-text-input__row">
              <label className="ff-text-input__field">
                <span className="ff-text-input__label">
                  {t('forms.workerRegistry.constructionExperienceYears.yearsLabel')}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  className={`ff-text-input__input${numberError ? ' ff-text-input__input--error' : ''}`}
                  value={value}
                  onChange={(e) => onChange(step.fieldName, e.target.value)}
                  autoComplete="off"
                  aria-invalid={Boolean(numberError)}
                />
              </label>
              {numberError && (
                <div className="ff-text-input__error" role="alert">
                  {numberError}
                </div>
              )}
            </div>
          </div>
          <div className="ff-text-input__footer">
            <button
              type="button"
              className="ff-text-input__send"
              onClick={() => {
                setSubmitAttempted(true);
                if (canSendExperience) onSubmit();
              }}
              disabled={!canSendExperience}
              aria-label={sendAriaLabel}
            >
              <img src={sendIcon} alt="" aria-hidden />
            </button>
          </div>
        </div>
      );
    }

    return (
      <TextInput
        fields={[
          {
            id: step.fieldName,
            label: textFieldLabel,
            required: step.required,
            maxLength: step.max ?? 10,
            validate: (raw) => {
              const v = raw.trim();
              if (!v) return step.required ? t('submitDocuments.workerRegistryChat.required') : null;
              if (!/^\d+$/.test(v)) return t('submitDocuments.workerRegistryChat.invalidNumber');
              const n = Number(v);
              if (step.min != null && n < step.min) return t('submitDocuments.workerRegistryChat.invalidNumber');
              if (step.max != null && n > step.max) return t('submitDocuments.workerRegistryChat.invalidNumber');
              return null;
            },
          },
        ]}
        values={{ [step.fieldName]: value }}
        onChange={(id, v) => onChange(id, v)}
        onSubmit={onSubmit}
        submitDisabled={!canSubmit}
        submitAriaLabel={sendAriaLabel}
        requiredMessage={t('submitDocuments.workerRegistryChat.required')}
      />
    );
  }

  // text (default)
  return (
    <TextInput
      fields={[
        {
          id: step.fieldName,
          label: textFieldLabel,
          required: step.required,
          maxLength: step.maxLength ?? 500,
        },
      ]}
      values={{ [step.fieldName]: value }}
      onChange={(id, v) => onChange(id, v)}
      onSubmit={onSubmit}
      submitDisabled={!canSubmit}
      submitAriaLabel={sendAriaLabel}
      requiredMessage={t('submitDocuments.workerRegistryChat.required')}
    />
  );
};

export default WorkerRegistryComposer;
